<template>
  <section class="contact-section" id="contact">
    <!-- Hero Header -->
    <div class="hero-header">
      <div class="hero-content">
        <h1 class="hero-title">Contact Us</h1>
        <p class="hero-subtitle">Get in touch with The Players League DFW</p>
      </div>
    </div>

    <!-- Main Content -->
    <div class="container">
      <!-- Welcome Card -->
      <div class="welcome-card">
        <div class="card-icon">
          <Icon name="heroicons:chat-bubble-left-ellipsis" />
        </div>
        <h2>We're Here to Help</h2>
        <p>Whether you're interested in joining our league, have questions about team registration, or need general information, we'd love to hear from you. Reach out through any of the methods below.</p>
      </div>

      <!-- Contact Methods Grid -->
      <div class="contact-grid">
        <!-- Email Contact -->
        <div class="contact-card">
          <div class="contact-icon">
            <Icon name="heroicons:envelope" />
          </div>
          <h3>Email Us</h3>
          <p>Best for detailed inquiries and league information</p>
          <a href="mailto:theplayersleaguedfw@gmail.com" class="contact-link">
            theplayersleaguedfw@gmail.com
          </a>
        </div>

        <!-- Phone Contact -->
        <div class="contact-card">
          <div class="contact-icon">
            <Icon name="heroicons:phone" />
          </div>
          <h3>Call or Text</h3>
          <p>Quick questions and immediate assistance</p>
          <a href="tel:8176555758" class="contact-link">
            (817) 655-5758
          </a>
        </div>

        <!-- Facebook Group -->
        <div class="contact-card">
          <div class="contact-icon">
            <Icon name="heroicons:user-group" />
          </div>
          <h3>Facebook Group</h3>
          <p>Join our community and stay updated</p>
          <a href="https://www.facebook.com/groups/2431630200504644" target="_blank" rel="noopener noreferrer" class="contact-link">
            The Players League DFW Group
          </a>
        </div>

        <!-- Jim Dixon Facebook -->
        <div class="contact-card">
          <div class="contact-icon">
            <Icon name="heroicons:user-circle" />
          </div>
          <h3>League Director</h3>
          <p>Connect directly with our league director</p>
          <a href="https://www.facebook.com/jimdixon76033" target="_blank" rel="noopener noreferrer" class="contact-link">
            Jim Dixon Facebook
          </a>
        </div>
      </div>

      <!-- Response Information -->
      <div class="response-info-card">
        <div class="card-icon">
          <Icon name="heroicons:clock" />
        </div>
        <h3>Response Times</h3>
        <div class="response-details">
          <div class="response-item">
            <Icon name="heroicons:envelope" class="response-icon" />
            <div>
              <strong>Email:</strong>
              <span>Within 24 hours</span>
            </div>
          </div>
          <div class="response-item">
            <Icon name="heroicons:phone" class="response-icon" />
            <div>
              <strong>Phone/Text:</strong>
              <span>Same day response</span>
            </div>
          </div>
          <div class="response-item">
            <Icon name="heroicons:user-group" class="response-icon" />
            <div>
              <strong>Facebook:</strong>
              <span>Community discussions ongoing</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
// No script needed for this static component
</script>

<style scoped>
.contact-section {
  position: relative;
  min-height: 100vh;
  background: 
    linear-gradient(135deg, rgba(30, 58, 138, 0.95) 0%, rgba(15, 23, 42, 0.9) 100%),
    url('/images/billiards-1-1920w.webp');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
}

.hero-header {
  position: relative;
  height: 40vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: linear-gradient(135deg, rgba(30, 58, 138, 0.9) 0%, rgba(15, 23, 42, 0.8) 100%);
}

.hero-content {
  z-index: 2;
}

.hero-title {
  font-size: 4rem;
  font-weight: 800;
  color: var(--gold);
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.hero-subtitle {
  font-size: 1.5rem;
  color: var(--white);
  font-weight: 300;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
}

.welcome-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  margin-bottom: 4rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.card-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  box-shadow: 0 10px 30px rgba(212, 175, 55, 0.3);
}

.card-icon svg {
  width: 40px;
  height: 40px;
  color: var(--white);
}

.welcome-card h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--white);
  margin-bottom: 1.5rem;
}

.welcome-card p {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
}

.contact-card {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: 2.5rem 2rem;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.contact-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
  border-color: var(--gold);
}

.contact-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--royal-blue) 0%, var(--navy-blue) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  box-shadow: 0 8px 25px rgba(30, 58, 138, 0.3);
}

.contact-icon svg {
  width: 28px;
  height: 28px;
  color: var(--white);
}

.contact-card h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--white);
  margin-bottom: 1rem;
}

.contact-card p {
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
}

.contact-link {
  display: inline-block;
  padding: 0.8rem 1.5rem;
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  color: var(--white);
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);
}

.contact-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(212, 175, 55, 0.4);
}

.response-info-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.response-info-card h3 {
  font-size: 2rem;
  font-weight: 600;
  color: var(--white);
  margin-bottom: 2rem;
}

.response-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.response-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.response-icon {
  width: 24px;
  height: 24px;
  color: var(--gold);
  flex-shrink: 0;
}

.response-item div {
  text-align: left;
}

.response-item strong {
  display: block;
  color: var(--white);
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.response-item span {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .hero-header {
    height: 30vh; /* Reduced from 40vh */
  }
  
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-subtitle {
    font-size: 1.2rem;
  }
  
  .container {
    padding: 2rem 1rem;
  }
  
  .welcome-card {
    padding: 2rem;
    margin-bottom: 3rem;
  }
  
  .welcome-card h2 {
    font-size: 2rem;
  }
  
  .contact-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .contact-card {
    padding: 2rem 1.5rem;
  }
  
  .response-info-card {
    padding: 2rem;
  }
  
  .response-details {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .response-item {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
  
  .response-item div {
    text-align: center;
  }
}

@media (max-width: 480px) {
  .hero-header {
    height: 25vh; /* More compact on small phones */
    padding: 1.5rem 1rem;
  }
  
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1rem;
  }
  
  .contact-grid {
    grid-template-columns: 1fr;
  }
  
  .card-icon {
    width: 60px;
    height: 60px;
  }
  
  .card-icon svg {
    width: 30px;
    height: 30px;
  }
}

/* Extra small devices */
@media (max-width: 360px) {
  .hero-header {
    height: 22vh;
  }
  
  .hero-title {
    font-size: 1.8rem;
  }
  
  .welcome-card {
    padding: 1.5rem;
  }
  
  .welcome-card h2 {
    font-size: 1.7rem;
  }
}
</style>
